/**
 * Product Learning Config Editor
 *
 * Product 레벨 학습 활동(시작/종료) 설정 에디터
 * 비디오 유형 선택 시 동영상 자료실에서 동영상을 선택할 수 있도록 연동
 */
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, CheckCircle, Video, FileText, Settings, Film, Loader2, Lock } from 'lucide-react'
import type { ProductLearningConfig, ScopedActivityConfig, ScopedQuizOptions } from '@/types/configuration'
import { getMediaLibrarySimple, type MediaLibrarySimpleResponse } from '@/api/configuration'

interface ProductLearningConfigEditorProps {
  config: ProductLearningConfig | undefined
  onUpdate: (config: ProductLearningConfig) => void
  readOnly?: boolean
}

const defaultQuizOptions: ScopedQuizOptions = {
  questionCount: 20,
  shuffleQuestions: true,
  passingScore: 70,
  timeLimitMinutes: undefined,
}

const defaultActivityConfig: ScopedActivityConfig = {
  enabled: false,
  types: ['quiz'],
  quizOptions: { ...defaultQuizOptions },
}

export const ProductLearningConfigEditor: React.FC<ProductLearningConfigEditorProps> = ({
  config,
  onUpdate,
  readOnly,
}) => {
  const { t } = useTranslation('configuration')
  const [expandedSection, setExpandedSection] = useState<'start' | 'end' | null>('start')
  const [videoList, setVideoList] = useState<MediaLibrarySimpleResponse[]>([])
  const [videoListLoading, setVideoListLoading] = useState(false)
  const [videoListLoaded, setVideoListLoaded] = useState(false)

  const startActivity = config?.startActivity || { ...defaultActivityConfig }
  const endActivity = config?.endActivity || { ...defaultActivityConfig }

  // 비디오 유형이 선택되면 동영상 자료실 목록 로드
  useEffect(() => {
    const needsVideoList = (startActivity.types?.includes('video') && startActivity.enabled) ||
      (endActivity.types?.includes('video') && endActivity.enabled)

    if (needsVideoList && !videoListLoaded) {
      loadVideoList()
    }
  }, [startActivity.types, startActivity.enabled, endActivity.types, endActivity.enabled])

  const loadVideoList = async () => {
    setVideoListLoading(true)
    try {
      const data = await getMediaLibrarySimple()
      setVideoList(data)
      setVideoListLoaded(true)
    } catch (err) {
      console.error('동영상 자료실 로딩 실패:', err)
    } finally {
      setVideoListLoading(false)
    }
  }

  const handleStartActivityUpdate = (activity: ScopedActivityConfig) => {
    onUpdate({
      ...config,
      startActivity: activity,
      endActivity: config?.endActivity,
    })
  }

  const handleEndActivityUpdate = (activity: ScopedActivityConfig) => {
    onUpdate({
      ...config,
      startActivity: config?.startActivity,
      endActivity: activity,
    })
  }

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: t('productLearning.description') }} />
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 p-3 bg-gray-100 border border-gray-200 rounded-lg">
          <Lock className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">{t('productLearning.readOnlyMessage')}</span>
        </div>
      )}

      {!readOnly && (<>
      {/* 시작 활동 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'start' ? null : 'start')}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Play className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-gray-900">{t('productLearning.startActivity')}</span>
            {startActivity.enabled && (
              <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                {t('productLearning.enabled')}
              </span>
            )}
          </div>
          <Settings className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'start' ? 'rotate-90' : ''}`} />
        </button>

        {expandedSection === 'start' && (
          <div className="p-4 space-y-4">
            <ActivityConfigSection
              activity={startActivity}
              onUpdate={handleStartActivityUpdate}
              label={t('productLearning.startActivity')}
              type="start"
              videoList={videoList}
              videoListLoading={videoListLoading}
              onRefreshVideoList={() => { setVideoListLoaded(false); loadVideoList() }}
            />
          </div>
        )}
      </div>

      {/* 종료 활동 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'end' ? null : 'end')}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">{t('productLearning.endActivity')}</span>
            {endActivity.enabled && (
              <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                {t('productLearning.enabled')}
              </span>
            )}
          </div>
          <Settings className={`w-4 h-4 text-gray-400 transition-transform ${expandedSection === 'end' ? 'rotate-90' : ''}`} />
        </button>

        {expandedSection === 'end' && (
          <div className="p-4 space-y-4">
            <ActivityConfigSection
              activity={endActivity}
              onUpdate={handleEndActivityUpdate}
              label={t('productLearning.endActivity')}
              type="end"
              videoList={videoList}
              videoListLoading={videoListLoading}
              onRefreshVideoList={() => { setVideoListLoaded(false); loadVideoList() }}
            />
          </div>
        )}
      </div>
      </>)}
    </div>
  )
}

// 활동 설정 섹션 컴포넌트
interface ActivityConfigSectionProps {
  activity: ScopedActivityConfig
  onUpdate: (activity: ScopedActivityConfig) => void
  label: string
  type: 'start' | 'end'
  videoList: MediaLibrarySimpleResponse[]
  videoListLoading: boolean
  onRefreshVideoList: () => void
}

const ActivityConfigSection: React.FC<ActivityConfigSectionProps> = ({
  activity,
  onUpdate,
  label,
  type,
  videoList,
  videoListLoading,
  onRefreshVideoList,
}) => {
  const { t } = useTranslation('configuration')
  const handleToggle = () => {
    onUpdate({
      ...activity,
      enabled: !activity.enabled,
    })
  }

  const handleTypeToggle = (toggleType: 'video' | 'quiz', checked: boolean) => {
    const currentTypes = activity.types ?? []
    const newTypes = checked
      ? [...currentTypes, toggleType]
      : currentTypes.filter(t => t !== toggleType)
    onUpdate({
      ...activity,
      types: newTypes,
      quizOptions: toggleType === 'quiz'
        ? (checked ? (activity.quizOptions || { ...defaultQuizOptions }) : undefined)
        : activity.quizOptions,
      videoContentId: toggleType === 'video'
        ? (checked ? activity.videoContentId : undefined)
        : activity.videoContentId,
    })
  }

  const handleQuizOptionsChange = (options: Partial<ScopedQuizOptions>) => {
    onUpdate({
      ...activity,
      quizOptions: {
        ...(activity.quizOptions || defaultQuizOptions),
        ...options,
      },
    })
  }

  return (
    <div className="space-y-4">
      {/* 활성화 토글 */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={activity.enabled}
          onChange={handleToggle}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">{t('productLearning.useActivity', { label })}</span>
      </label>

      {activity.enabled && (
        <>
          {/* 활동 유형 선택 (복수 선택 가능) - 시작: 비디오→퀴즈 / 종료: 퀴즈→비디오 */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{t('productLearning.activityTypeLabel')}</label>
            <div className="flex gap-4">
              {type === 'start' ? (<>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activity.types?.includes('video') ?? false}
                    onChange={(e) => handleTypeToggle('video', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Video className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">{t('productLearning.videoType')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activity.types?.includes('quiz') ?? false}
                    onChange={(e) => handleTypeToggle('quiz', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <FileText className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-700">{t('productLearning.quizType')}</span>
                </label>
              </>) : (<>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activity.types?.includes('quiz') ?? false}
                    onChange={(e) => handleTypeToggle('quiz', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <FileText className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-700">{t('productLearning.quizType')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activity.types?.includes('video') ?? false}
                    onChange={(e) => handleTypeToggle('video', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Video className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">{t('productLearning.videoType')}</span>
                </label>
              </>)}
            </div>
          </div>

          {/* 옵션 패널: 시작활동은 비디오→퀴즈, 종료활동은 퀴즈→비디오 순서 */}
          {(type === 'start' ? ['video', 'quiz'] as const : ['quiz', 'video'] as const).map(optionType => (
            optionType === 'quiz' ? (
              activity.types?.includes('quiz') && (
                <div key="quiz" className="p-4 space-y-4 bg-gray-50 rounded-lg ml-6">
                  <h4 className="text-sm font-medium text-gray-900">{t('productLearning.quizOptions')}</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">{t('productLearning.questionCount')}</label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={activity.quizOptions?.questionCount ?? 20}
                        onChange={(e) => handleQuizOptionsChange({ questionCount: parseInt(e.target.value) || 20 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 mb-1">{t('productLearning.passingScore')}</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={activity.quizOptions?.passingScore ?? 70}
                        onChange={(e) => handleQuizOptionsChange({ passingScore: parseInt(e.target.value) || 70 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 mb-1">{t('productLearning.timeLimit')}</label>
                      <input
                        type="number"
                        min={0}
                        max={180}
                        value={activity.quizOptions?.timeLimitMinutes ?? 0}
                        onChange={(e) => handleQuizOptionsChange({ timeLimitMinutes: parseInt(e.target.value) || undefined })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activity.quizOptions?.shuffleQuestions ?? true}
                      onChange={(e) => handleQuizOptionsChange({ shuffleQuestions: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('productLearning.shuffleQuestions')}</span>
                  </label>
                </div>
              )
            ) : (
              activity.types?.includes('video') && (
                <div key="video" className="p-4 space-y-4 bg-gray-50 rounded-lg ml-6">
                  <h4 className="text-sm font-medium text-gray-900">{t('productLearning.videoOptions')}</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('productLearning.selectVideo')}
                    </label>

                    {videoListLoading ? (
                      <div className="flex items-center gap-2 px-3 py-4 text-sm text-gray-500 border border-gray-200 rounded-lg bg-white">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('productLearning.videoListLoading')}
                      </div>
                    ) : videoList.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg bg-white text-center">
                        <Film className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        {t('productLearning.noVideos')}
                        <p className="text-xs mt-1 text-gray-400">
                          {t('productLearning.noVideosHint')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          value={activity.videoContentId ?? ''}
                          onChange={(e) => {
                            const val = e.target.value
                            onUpdate({
                              ...activity,
                              videoContentId: val ? parseInt(val) : undefined,
                            })
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        >
                          <option value="">{t('productLearning.selectVideoPlaceholder')}</option>
                          {videoList.map(video => (
                            <option key={video.id} value={video.id}>
                              {video.title}
                              {video.durationSeconds ? ` (${Math.floor(video.durationSeconds / 60)}:${(video.durationSeconds % 60).toString().padStart(2, '0')})` : ''}
                            </option>
                          ))}
                        </select>

                        {activity.videoContentId && (
                          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                            <Video className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="text-blue-800">
                              {t('productLearning.selected', { title: videoList.find(v => v.id === activity.videoContentId)?.title || `ID: ${activity.videoContentId}` })}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            {t('productLearning.videoLibraryHint')}
                          </p>
                          <button
                            type="button"
                            onClick={onRefreshVideoList}
                            className="text-xs text-blue-600 hover:text-blue-700 underline"
                          >
                            {t('productLearning.refresh')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            )
          ))}
        </>
      )}
    </div>
  )
}

export default ProductLearningConfigEditor
